import React from 'react';
import { Link } from 'react-router-dom';
import { Home, Search, BookOpen, MessageCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';
import { Input } from '../components/ui/input';

const NotFoundPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = React.useState('');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      // Redirect to homepage with search context
      window.location.href = `/?search=${encodeURIComponent(searchTerm.trim())}`;
    }
  };

  return (
    <>
      <SEOHead 
        title="Page Not Found - 404"
        description="The requested page could not be found. Explore Memara's AI memory platform with our navigation options."
        keywords={["404", "page not found", "memara", "AI memory", "navigation"]}
        noIndex={true}
      />
      
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center px-4">
        <div className="max-w-2xl mx-auto text-center">
          {/* 404 Visual */}
          <div className="mb-8">
            <h1 className="text-9xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
              404
            </h1>
            <p className="text-2xl font-semibold text-gray-900 mt-4">
              Page Not Found
            </p>
            <p className="text-gray-600 mt-2 max-w-md mx-auto">
              The page you're looking for doesn't exist or has been moved. 
              Let's help you find what you need.
            </p>
          </div>

          {/* Search Bar */}
          <div className="mb-8">
            <form onSubmit={handleSearch} className="max-w-md mx-auto">
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="Search for what you're looking for..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1"
                />
                <Button type="submit" size="default">
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </form>
          </div>

          {/* Navigation Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <Button asChild variant="outline" size="lg" className="flex items-center gap-2 h-auto p-4">
              <Link to="/">
                <Home className="w-5 h-5" />
                <div className="text-left">
                  <div className="font-semibold">Go Home</div>
                  <div className="text-sm text-gray-600">Return to homepage</div>
                </div>
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="flex items-center gap-2 h-auto p-4">
              <Link to="/blog">
                <BookOpen className="w-5 h-5" />
                <div className="text-left">
                  <div className="font-semibold">Read Blog</div>
                  <div className="text-sm text-gray-600">AI memory insights</div>
                </div>
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="flex items-center gap-2 h-auto p-4">
              <Link to="/docs">
                <BookOpen className="w-5 h-5" />
                <div className="text-left">
                  <div className="font-semibold">Documentation</div>
                  <div className="text-sm text-gray-600">Integration guides and links</div>
                </div>
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="flex items-center gap-2 h-auto p-4">
              <Link to="/support">
                <MessageCircle className="w-5 h-5" />
                <div className="text-left">
                  <div className="font-semibold">Get Help</div>
                  <div className="text-sm text-gray-600">Contact support</div>
                </div>
              </Link>
            </Button>
          </div>

          {/* Popular Pages */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">Popular Pages</h3>
            <div className="flex flex-wrap gap-2 justify-center">
              <Button asChild variant="ghost" size="sm">
                <Link to="/integrations/chatgpt">ChatGPT Integration</Link>
              </Button>
              <Button asChild variant="ghost" size="sm">
                <Link to="/integrations/claude-desktop">Claude Desktop</Link>
              </Button>
              <Button asChild variant="ghost" size="sm">
                <Link to="/integrations/zapier">Zapier</Link>
              </Button>
              <Button asChild variant="ghost" size="sm">
                <Link to="/roadmap">Product Roadmap</Link>
              </Button>
            </div>
          </div>

          {/* Back Button */}
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </Button>
        </div>
      </div>
    </>
  );
};

export default NotFoundPage; 